
import React from "react";
import { Percent } from "lucide-react";

interface WellnessBannerProps {
    title: string;
    highlightText: string;
    description: string;
    price: string;
    yearlyText?: string;
    savingsText: string;
    badgeText?: string;
    image: string;
}

const WellnessBanner: React.FC<WellnessBannerProps> = ({
    title,
    highlightText,
    description,
    price,
    yearlyText = "/year",
    savingsText,
    badgeText = "Expat Choice",
    image,
}) => {
    return (
        <div className="rounded-[3rem] overflow-hidden border-4 border-accent p-12 relative shadow-2xl group">

            {/* Background Image */}
            <img
                src={image}
                alt={title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
            />

            {/* Left Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-logoHeading/95 via-logoHeading/70 to-transparent" />

            {/* Right Blur Overlay */}
            <div className="absolute inset-y-0 right-0 w-1/2 bg-white/10 backdrop-blur-sm group-hover:bg-white/20 transition-all duration-500" />

            {/* Glow Effect */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent/20 blur-[80px]" />

            {/* Top Badge */}
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-accent text-logoHeading px-8 py-2 rounded-full font-black text-xs uppercase tracking-[0.2em] shadow-lg z-20">
                {badgeText}
            </div>

            {/* Main Content */}
            <div className="relative z-20 max-w-6xl mx-auto text-center">

                {/* Title */}
                <h2 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight">
                    {title}
                    <br />
                    <span className="text-mainBrandColor italic">
                        {highlightText}
                    </span>
                </h2>

                {/* Description */}
                <p className="text-white opacity-90 max-w-xl mx-auto mb-12 text-description italic leading-relaxed">
                    {description}
                </p>

                <div className="flex justify-center items-center">

                    {/* Price Box */}
                    <div className="text-left p-8 rounded-3xl border border-mediumgray shadow-inner backdrop-blur-md bg-black/20 group-hover:-translate-y-2 transition-transform duration-500">

                        <p className="text-smallText font-bold text-white">
                            One Payment{" "}
                            <span className="text-mainBrandColor">
                                Zero Worries
                            </span>
                        </p>

                        <p className="text-3xl font-black text-white mt-2">
                            {price}
                            <span className="text-lg text-gray-300 font-normal">
                                {yearlyText}
                            </span>
                        </p>

                        <div className="bg-offerColor text-logoHeading font-bold mt-3 px-3 py-2 rounded-md uppercase text-xs tracking-widest inline-flex items-center gap-2">
                            <Percent size={12} />
                            {savingsText}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WellnessBanner;